import { useState } from 'react'

import { Box, Button, Heading, Text } from '@chakra-ui/react'

import {
  useInterview,
  useRegenerateSummary,
} from '@/react-query/interview/interview'
import { useIsViewOnlyMember } from '@/react-query/team'
import { RefreshIcon } from '@/views/assets/RefreshIcon'

/**
 * Shown when an interview has a transcript but no summary parts — most often
 * because the interview was never matched to a job, so nothing was generated
 * automatically. Without this the Summary tab renders as a blank page.
 */
export const EmptySummary = () => {
  const interview = useInterview().data
  const isViewOnlyUser = useIsViewOnlyMember()
  const regenerateSummary = useRegenerateSummary()
  const [isGenerating, setIsGenerating] = useState(false)

  const hasRole = Boolean(interview?.roleId)
  const hasTranscript = Boolean(interview?.fullTranscript)

  const description = (() => {
    if (!hasTranscript) {
      return 'The summary will appear here once the interview is transcribed.'
    }
    if (!hasRole) {
      return 'This interview is not linked to a job, so no summary was generated. You can generate it now — or pick a job in the "Job description" card first, for a summary tailored to that role.'
    }
    return 'No summary has been generated for this interview yet.'
  })()

  return (
    <Box
      className={
        'border-[1px] border-[#E0E0E0] rounded-[2.5rem] py-[1.75rem] px-[2.5rem] mb-5'
      }
      boxSizing="border-box"
    >
      <Heading fontWeight="bold" fontSize="1.25rem" as="h2">
        No summary yet
      </Heading>
      <Text mt={3} color={'#616161'}>
        {description}
      </Text>
      {!isViewOnlyUser && hasTranscript && (
        <Button
          mt={6}
          variant={'brand-solid'}
          leftIcon={<RefreshIcon />}
          isLoading={isGenerating}
          onClick={async () => {
            try {
              setIsGenerating(true)
              await regenerateSummary.mutateAsync()
            } finally {
              setIsGenerating(false)
            }
          }}
        >
          Generate summary
        </Button>
      )}
    </Box>
  )
}
